import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarDays, Clock, Loader2, Pencil, User as UserIcon, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { aiApi } from '@/utils/aiApi';
import { commsApi } from '@/utils/commsApi';
import { STATUS_OPTIONS, formatDue, useMoveTask } from '@/components/ai/TaskCardStrip';

// Quick view of a task inside the chat: details, subtasks (tick-off) and Move to; full editing stays in Kanban.
export const TaskPeekDialog = ({ taskId, onClose, onChanged }) => {
  const [task, setTask] = useState(null);
  const [error, setError] = useState('');
  const { move, moving } = useMoveTask((card) => { setTask((t) => ({ ...t, ...card })); onChanged?.(card); });

  useEffect(() => {
    if (!taskId) return undefined;
    let cancelled = false;
    setTask(null);
    setError('');
    aiApi.task(taskId)
      .then((t) => { if (!cancelled) setTask(t); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.detail || 'Could not load this task'); });
    return () => { cancelled = true; };
  }, [taskId]);

  const toggleSubtask = async (index, done) => {
    const prev = task.subtasks;
    const next = prev.map((s, i) => (i === index ? { ...s, done } : s));
    setTask((t) => ({ ...t, subtasks: next, subtasks_done: next.filter((s) => s.done).length }));
    try {
      await commsApi.toggleSubtask(task.id, index, done);
      onChanged?.({ id: task.id, subtasks_done: next.filter((s) => s.done).length, subtasks_total: next.length });
    } catch (err) {
      setTask((t) => ({ ...t, subtasks: prev, subtasks_done: prev.filter((s) => s.done).length }));
      toast.error(err?.response?.data?.detail || 'Could not update the subtask');
    }
  };

  return (
    <Dialog open={Boolean(taskId)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="task-peek" data-testid="task-peek-dialog">
        {!task && !error && (
          <div className="task-peek-loading"><Loader2 size={18} className="animate-spin" /> Loading task…</div>
        )}
        {error && <p className="form-error" role="alert" data-testid="task-peek-error">{error}</p>}
        {task && (
          <>
            <DialogHeader>
              <DialogTitle data-testid="task-peek-title">{task.title}</DialogTitle>
              <DialogDescription className="task-peek-team"><Users size={13} /> {task.team_name}</DialogDescription>
            </DialogHeader>

            <div className="task-peek-row">
              <Select value={task.status} onValueChange={(v) => move(task, v)} disabled={moving}>
                <SelectTrigger className="task-peek-status" aria-label="Move task" data-testid="task-peek-move">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`task-peek-move-${o.value}`}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {task.priority && <span className={`chat-task-priority ${task.priority}`}>{task.priority} priority</span>}
              {!task.is_owner && <span className="task-peek-hint">Moves are sent to the owner for approval</span>}
            </div>

            <p className="task-peek-description" data-testid="task-peek-description">{task.description || 'No description.'}</p>

            <div className="task-peek-meta">
              <span><UserIcon size={13} /> {task.assignee_name || 'Unassigned'}</span>
              {task.due_date && <span><CalendarDays size={13} /> Due {formatDue(task.due_date)}</span>}
              {task.estimated_hours != null && <span><Clock size={13} /> {task.estimated_hours} h</span>}
            </div>

            {task.subtasks?.length > 0 && (
              <div className="task-peek-subtasks" data-testid="task-peek-subtasks">
                <div className="task-peek-subtasks-head">Subtasks · {task.subtasks.filter((s) => s.done).length}/{task.subtasks.length}</div>
                {task.subtasks.map((s, i) => (
                  <label key={`${task.id}-${i}`} className={`task-peek-subtask ${s.done ? 'done' : ''}`}>
                    <Checkbox checked={Boolean(s.done)} onCheckedChange={(v) => toggleSubtask(i, Boolean(v))} data-testid={`task-peek-subtask-${i}`} />
                    <span>{s.title}</span>
                  </label>
                ))}
              </div>
            )}

            <div className="task-peek-footer">
              <Link to="/dashboard/kanban" className="task-peek-edit" data-testid="task-peek-edit-kanban"><Pencil size={13} /> Edit in Kanban</Link>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
